import cx from 'clsx';
import React from 'react';
import { Tooltip } from 'react-tooltip';

import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem } from '@model/actionItem';

import { useModalActions } from '@store/useModalStore';

import type { IAiControls } from '../../model/IAiControls';
import { useCancelSnoozeMutation } from '../../services/useCancelSnoozeMutation';
import style from './style.module.scss';

interface IProps {
  actionItem: IActionItem;
  controls: IAiControls;
}

const AIActionButtons: React.FC<IProps> = ({ actionItem, controls }) => {
  const { t } = useTranslation();

  const { setActiveModal } = useModalActions();

  const { mutate: cancelSnoozeMutate } = useCancelSnoozeMutation({
    actionItemId: actionItem.id,
  });

  const facilityTimezone = actionItem?.facility?.timezone?.offset;

  const handleCancelSnooze = () => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Remove snooze'),
        bodyContent: (
          <div className={style.snoozeBodyText}>
            {t('Are you sure you want to cancel Action Item snooze?')}
          </div>
        ),
        confirmText: t('Remove'),
        onConfirm: () =>
          cancelSnoozeMutate(
            { action_item_id: +actionItem.id },
            {
              onSuccess: () => {
                showToast({
                  variant: 'success',
                  message: t('The snooze that was applied to the action item has been canceled.'),
                });
              },
            },
          ),
      },
    });
  };

  const handleEditSnooze = () => {
    setActiveModal({
      name: MODAL_NAMES.editSnoozeModal,
      options: {
        actionItemId: +actionItem.id,
        facilityTimezone: facilityTimezone,
        remindDate: actionItem.remindDate || '',
      },
    });
  };

  const handleOpenSnoozeModal = () => {
    if (!controls.isOnboardingMockupAi) {
      setActiveModal({
        name: MODAL_NAMES.snoozeModal,
        options: { actionItemId: actionItem.id, facilityTimezone: facilityTimezone },
      });
    }
  };

  const handleOpenCompletionReportModal = () => {
    if (!controls.isOnboardingMockupAi) {
      setActiveModal({
        name: MODAL_NAMES.completionReportModal,
        options: { actionItemId: actionItem.id, controls },
      });
    }
  };

  return (
    //hide this block on print ai details page
    <div className={cx(style.actionButtonsBlock, 'hideOnPrint')}>
      {controls?.showSnoozeBtn && (
        <Button
          id="tourStep-16"
          type="outline"
          text={t('Snooze')}
          variant={'full'}
          onClick={handleOpenSnoozeModal}
          dataTooltipId={'ai-actions'}
          dataTooltipContent={t(
            'Snooze this action item until a specified date/time (e.g., if you are awaiting parts or experiencing downtime). Waites will continue monitoring during the snooze and may end it if the severity increases.',
          )}
        />
      )}
      {controls?.showCancelSnoozeBtn && (
        <Button type="outline" text={t('Edit snooze')} variant={'full'} onClick={handleEditSnooze} />
      )}
      {controls?.showCancelSnoozeBtn && (
        <Button type="outline" text={t('Cancel snooze')} variant={'full'} onClick={handleCancelSnooze} />
      )}
      {(controls?.showCloseBtn || controls?.showCancelAndCloseBtn) && (
        <Button
          id="tourStep-17"
          type="primary"
          text={t('Close action item')}
          onClick={handleOpenCompletionReportModal}
          dataTooltipId={'ai-actions'}
          dataTooltipContent={t(
            'Submit a completion report after the necessary maintenance has been completed and you believe the issue has been resolved. Waites will confirm the issue is resolved and may re-open the action item with additional guidance if needed.',
          )}
        />
      )}
      {(controls?.showEditReportBtn || controls?.showWwEditReportBtn) && (
        <Button type="outline" text={t('Edit report')} onClick={handleOpenCompletionReportModal} />
      )}
      {(controls?.showResubmitReportBtn || controls?.showWwResubmitReportBtn) && (
        <Button
          id="tourStep-17"
          type="primary"
          text={t('Resubmit report')}
          onClick={handleOpenCompletionReportModal}
        />
      )}
      <Tooltip id="ai-actions" className={style.btnTooltip} place="top-start" />
    </div>
  );
};

export default AIActionButtons;
